import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Router } from '@angular/router'
import { environment } from '../../../environments/environment'
import { jwtDecode } from 'jwt-decode'
import { BehaviorSubject, Observable } from 'rxjs'
import { Profile, TokenSession } from '../../models/users.interface'
import { DecodedToken } from '../../models/auth.interface'

export type LoginPayload = {
    username: string
    password: string
}

export type GoogleLoginResponse = {
    access: string
    refresh: string
    user: {
        id: number
        email: string
        first_name: string
        last_name: string
    }
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private apiUrl = environment.service_1 + 'auth_user/token/'
    private googleApiUrl = environment.service_1 + 'auth_user/google-login/'
    private getMyProfileUrl = environment.service_1 + 'auth_user/me'
    private token = new BehaviorSubject<TokenSession | null>(null)
    token$ = this.token.asObservable()
    private userId = new BehaviorSubject<number | null>(null)
    userId$ = this.userId.asObservable()
    private errorMsg = new BehaviorSubject<string | null>(null)
    errorMsg$ = this.errorMsg.asObservable()
    private clientId = environment.google_client_id

    constructor(
        private http: HttpClient,
        private router: Router
    ) {}

    /** Perform an HTTP request to retrieve the user profile using the current session */
    getMyProfile(): Observable<Profile> {
        return this.http.get<Profile>(this.getMyProfileUrl)
    }

    setError(msg: string | null) {
        this.errorMsg.next(msg)
    }

    login(data: LoginPayload) {
        return this.http.post<TokenSession>(this.apiUrl, data)
    }

    refresh(refresh: string): Observable<{ access: string }> {
        return this.http.post<{ access: string }>(`${this.apiUrl}refresh/`, {
            refresh,
        })
    }

    refreshToken(refresh: string): Observable<{ access: string }> {
        // const refresh = localStorage.getItem('refresh_token');
        return this.http.post<{ access: string }>(this.apiUrl + 'refresh/', {
            refresh,
        })
        // .pipe(map((res) => res.access));
    }

    storeAccessToken(token: string) {
        localStorage.setItem('access_token', token)
    }

    getAccessToken(): string | null {
        return localStorage.getItem('access_token')
    }

    setToken(token: TokenSession) {
        const decodedToken: DecodedToken = jwtDecode(token.access)
        this.setUserId(decodedToken.user_id)
        localStorage.setItem('access_token', token.access)
        localStorage.setItem('refresh_token', token.refresh)
        localStorage.setItem('userId', JSON.stringify(decodedToken.user_id))
        this.token.next(token)
    }

    setUserId(id: number | null) {
        if (id && id > 0) {
            this.userId.next(id)
        } else {
            this.userId.next(null)
        }
    }

    getToken() {
        const token = localStorage.getItem('token')
        return token ? (JSON.parse(token) as TokenSession) : null
    }

    getUserId() {
        const id = localStorage.getItem('userId')
        return id ? JSON.parse(id) : null
    }

    isAuthenticated(): boolean {
        const token = this.getToken()
        if (!token) {
            return false
        }
        const payload = JSON.parse(atob(token.access.split('.')[1]))
        const exp = payload.exp * 1000 // Convert to milliseconds
        return Date.now() < exp
    }

    initializeGoogleSignIn(): Promise<void> {
        return new Promise((resolve) => {
            if ((window as any).google) {
                ;(window as any).google.accounts.id.initialize({
                    client_id: this.clientId,
                    callback: this.handleCredentialResponse.bind(this),
                })
                resolve()
                return
            }
            const script = document.createElement('script')
            script.src = 'https://accounts.google.com/gsi/client'
            script.onload = () => {
                ;(window as any).google.accounts.id.initialize({
                    client_id: this.clientId,
                    callback: this.handleCredentialResponse.bind(this),
                })
                resolve()
            }
            document.head.appendChild(script)
        })
    }

    renderGoogleButton(element: HTMLElement) {
        ;(window as any).google.accounts.id.renderButton(element, {
            theme: 'outline',
            size: 'large',
        })
    }

    handleCredentialResponse(response: any) {
        this.http
            .post<GoogleLoginResponse>(this.googleApiUrl, {
                token: response.credential,
            })
            .subscribe((data) => {
                localStorage.setItem('access_token', data.access)
                localStorage.setItem('refresh_token', data.refresh)
                localStorage.setItem('token', JSON.stringify(data))
                const tokenSession: TokenSession = {
                    access: data.access,
                    refresh: data.refresh,
                }
                this.setToken(tokenSession)
                this.router.navigate(['home'])
            })
    }

    logout() {
        localStorage.clear()
        window.location.reload()
    }
}
